
import * as React from 'react';
import { action, observable } from 'mobx';
import {
	Model,
	IModelAttributes,
	attribute,
	entity,
	ReferencePath,
} from 'Models/Model';
import * as Models from 'Models/Entities';
import * as Validators from 'Validators';
import { CRUD } from '../CRUDOptions';
import * as AttrUtils from 'Util/AttributeUtils';
import { IAcl } from 'Models/Security/IAcl';
import {
	makeEnumFetchFunction,
	getCreatedModifiedCrudOptions,
} from 'Util/EntityUtils';
import UserSearchRequest from 'Models/Security/Acl/UserSearchRequest';
import VisitorsSearchRequest from 'Models/Security/Acl/VisitorsSearchRequest';
import * as Enums from '../Enums';
import { EntityFormMode } from 'Views/Components/Helpers/Common';
import { FileListPreview } from 'Views/Components/CRUD/Attributes/AttributeFile';
import SuperAdministratorScheme from '../Security/Acl/SuperAdministratorScheme';


export interface ISearchRequestEntityAttributes extends IModelAttributes {
	transactionId: string;
	transactionAmount: number;
	reportId: string;
	report: Blob;
	status: Enums.processingStatus;

	nevdisSearch?: Models.NevdisSearchEntity |
		Models.INevdisSearchEntityAttributes;
	ppsrSearch?: Models.PpsrSearchEntity |
		Models.IPpsrSearchEntityAttributes;
	businessEntityId: string;
	businessEntity: Models.BusinessEntity | Models.IBusinessEntityAttributes;
	servicePriceId: string;
	servicePrice: Models.ServicePriceEntity | Models.IServicePriceEntityAttributes;
	searchCriteria: string;
}

@entity('SearchRequestEntity', 'Search Request')
export default class SearchRequestEntity extends Model
	implements ISearchRequestEntityAttributes {
	public static acls: IAcl[] = [
		new SuperAdministratorScheme(),
		new UserSearchRequest(),
		new VisitorsSearchRequest(),

	];

	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [];

	@observable
	@attribute<SearchRequestEntity, string>()
	@CRUD({
		name: 'Transaction Id',
		displayType: 'textfield',
		order: 10,
		headerColumn: true,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public transactionId: string;
	

	
	@Validators.Numeric()
	@observable
	@attribute<SearchRequestEntity, number>()
	@CRUD({
		name: 'Transaction Amount',
		displayType: 'textfield',
		order: 20,
		headerColumn: true,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: AttrUtils.standardiseFloat,
	})
	public transactionAmount: number;

	@observable
	@attribute<SearchRequestEntity, string>({ file: 'report' })
	@CRUD({
		name: 'Report',
		displayType: 'file',
		order: 30,
		headerColumn: true,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: AttrUtils.standardiseUuid,
		fileAttribute: 'report',
		displayFunction: attr => attr
			? <FileListPreview url={attr} />
			: 'No File Attached',
	})
	public reportId: string;

	@observable
	public report: Blob;

	@observable
	@attribute<SearchRequestEntity, Enums.processingStatus>()
	@CRUD({
		name: 'Status',
		displayType: 'enum-combobox',
		order: 40,
		headerColumn: true,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: (attr: string) => {
			return AttrUtils.standardiseEnum(attr, Enums.processingStatusOptions);
		},
		enumResolveFunction: makeEnumFetchFunction(Enums.processingStatusOptions),
		displayFunction: (attr: Enums.processingStatus) => Enums.processingStatusOptions[attr],
	})
	public status: Enums.processingStatus;

	@observable
	@attribute({ isReference: true, manyReference: false })
	@CRUD({
		
		name: 'Nevdis Search',
		displayType: 'displayfield',
		order: 50,
		inputProps: {
			displayFunction: (model?: Models.NevdisSearchEntity) => model ? model.getDisplayName() : null,
		},
		
	})
	public nevdisSearch?: Models.NevdisSearchEntity;

	@observable
	@attribute({ isReference: true, manyReference: false })
	@CRUD({
		
		name: 'PPSR Search',
		displayType: 'displayfield',
		order: 60,
		inputProps: {
			displayFunction: (model?: Models.PpsrSearchEntity) => model ? model.getDisplayName() : null,
		},
		
	})
	public ppsrSearch?: Models.PpsrSearchEntity;

	@Validators.Required()
	@observable
	@attribute()
	@CRUD({
		
		name: 'Business Entity',
		displayType: 'reference-combobox',
		order: 70,
		referenceTypeFunc: () => Models.BusinessEntity,
		
	})
	public businessEntityId: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public businessEntity: Models.BusinessEntity;

	@Validators.Required()
	@observable
	@attribute()
	@CRUD({
		
		name: 'Service Price',
		displayType: 'reference-combobox',
		order: 80,
		referenceTypeFunc: () => Models.ServicePriceEntity,
		
	})
	public servicePriceId: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public servicePrice: Models.ServicePriceEntity;


	@observable
	@attribute<SearchRequestEntity, string>()
	@CRUD({
		name: 'Search Criteria',
		displayType: 'textfield',
		order: 90,
		headerColumn: true,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public searchCriteria: string;

	// eslint-disable-next-line @typescript-eslint/no-useless-constructor
	constructor(attributes?: Partial<ISearchRequestEntityAttributes>) {
		super(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	@action
	public assignAttributes(attributes?: Partial<ISearchRequestEntityAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.transactionId !== undefined) {
				this.transactionId = attributes.transactionId;
			}
			if (attributes.transactionAmount !== undefined) {
				this.transactionAmount = attributes.transactionAmount;
			}
			if (attributes.report !== undefined) {
				this.report = attributes.report;
			}
			if (attributes.reportId !== undefined) {
				this.reportId = attributes.reportId;
			}
			if (attributes.status !== undefined) {
				this.status = attributes.status;
			}
			if (attributes.nevdisSearch !== undefined) {
				if (attributes.nevdisSearch === null) {
					this.nevdisSearch = attributes.nevdisSearch;
				} else if (attributes.nevdisSearch instanceof Models.NevdisSearchEntity) {
					this.nevdisSearch = attributes.nevdisSearch;
				} else {
					this.nevdisSearch = new Models.NevdisSearchEntity(attributes.nevdisSearch);
				}
			}
			if (attributes.ppsrSearch !== undefined) {
				if (attributes.ppsrSearch === null) {
					this.ppsrSearch = attributes.ppsrSearch;
				} else if (attributes.ppsrSearch instanceof Models.PpsrSearchEntity) {
					this.ppsrSearch = attributes.ppsrSearch;
				} else {
					this.ppsrSearch = new Models.PpsrSearchEntity(attributes.ppsrSearch);
				}
			}
			if (attributes.businessEntityId !== undefined) {
				this.businessEntityId = attributes.businessEntityId;
			}
			if (attributes.businessEntity !== undefined) {
				if (attributes.businessEntity === null) {
					this.businessEntity = attributes.businessEntity;
				} else if (attributes.businessEntity instanceof Models.BusinessEntity) {
					this.businessEntity = attributes.businessEntity;
					this.businessEntityId = attributes.businessEntity.id;
				} else {
					this.businessEntity = new Models.BusinessEntity(attributes.businessEntity);
					this.businessEntityId = this.businessEntity.id;
				}
			}
			if (attributes.servicePriceId !== undefined) {
				this.servicePriceId = attributes.servicePriceId;
			}

			if (attributes.servicePrice !== undefined) {
				if (attributes.servicePrice === null) {
					this.servicePrice = attributes.servicePrice;
				} else if (attributes.servicePrice instanceof Models.ServicePriceEntity) {
					this.servicePrice = attributes.servicePrice;
					this.servicePriceId = attributes.servicePrice.id;
				} else {
					this.servicePrice = new Models.ServicePriceEntity(attributes.servicePrice);
					this.servicePriceId = this.servicePrice.id;
				}
			}

			if (attributes.searchCriteria !== undefined) {
				this.searchCriteria = attributes.searchCriteria;
			}
		}
	}

	/**
	 * Additional fields that are added to GraphQL queries when using the
	 * the managed model APIs.
	 */
	public defaultExpands = `
		nevdisSearch {
			${Models.NevdisSearchEntity.getAttributes().join('\n')}
		}
		ppsrSearch {
			${Models.PpsrSearchEntity.getAttributes().join('\n')}
		}
		businessEntity {
			${Models.BusinessEntity.getAttributes().join('\n')}
		}
		servicePrice {
			${Models.ServicePriceEntity.getAttributes().join('\n')}
		}
	`;

	/**
	 * The save method that is called from the admin CRUD components.
	 */
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	public async saveFromCrud(formMode: EntityFormMode) {
		const relationPath: ReferencePath = {
		};
		return this.save(
			relationPath,
			{
				options: [
					{
						key: 'mergeReferences',
						graphQlType: '[String]',
						value: [
						],
					},
				],
				contentType: 'multipart/form-data',
			},
		);
	}

	/**
	 * Returns the string representation of this entity to display on the UI.
	 */
	public getDisplayName() {
		return this.id;
	}
}

/*
 * Retrieve the created and modified CRUD attributes for defining the CRUD views and decorate the class with them.
 */
const [createdAttr, modifiedAttr] = getCreatedModifiedCrudOptions();
CRUD(createdAttr)(SearchRequestEntity.prototype, 'created');
CRUD(modifiedAttr)(SearchRequestEntity.prototype, 'modified');
